/**
 * 레인 J ④ — 내부 링크 «앵커 문구» 한 벌(순수 · DB·네트워크·env 0).
 *
 * 원칙
 *   · 문구는 키워드 지도(lib/seo/kw-map)에서 온다 — 현장 대표·보조 검색어, 시군구 검색 꼬리(DISTRICT_GLOBAL_ORDER), 시도 이름.
 *   · 자리마다 섞는다 — exact(대표 검색어 그대로) / partial(이름 + 다른 꼬리) / branded(이름만) / descriptive(사실 서술).
 *     비율(EXACT_SHARE 등)은 (출발 쪽, 도착 쪽) 해시로 고르므로 «한 도착 쪽으로 오는 링크 전체» 에서 exact ≤ 30% 에 수렴한다.
 *   · 같은 (출발, 도착) = 늘 같은 문구(결정적) → HTML 이 회차마다 흔들리지 않는다(모든 UA 같은 HTML).
 *   · 콘텐츠 링크에 「바로가기·더보기·여기·보기」 만으로 된 문구 금지(isGenericAnchor). 키워드 반복(같은 낱말 2회) 금지 · 28자 상한.
 *   · 한 쪽 안 같은 도착 주소는 앵커 1개(AnchorBudget) — 두 번째부터는 링크 없이 글자만.
 *   · 시군구 허브가 색인 제외(단지 프로필 < 10 · apt/area 페이지 MIN_PROFILES 와 같은 값)면 시도 허브로 건다(hubTarget).
 */
import { fnv1a } from '@/lib/cl-cards/pure';
import { DISTRICT_GLOBAL_ORDER, districtPlaceOf } from '@/lib/seo/kw-map';

export type AnchorStyle = 'exact' | 'partial' | 'branded' | 'descriptive';
export type AnchorTargetKind = 'site' | 'district' | 'region' | 'post' | 'list';

/** 자리 섞기 비율(합 100). exact 는 30% 상한 아래로 둔다. */
export const STYLE_MIX: ReadonlyArray<[AnchorStyle, number]> = [['exact', 25], ['partial', 30], ['branded', 25], ['descriptive', 20]];
export const EXACT_SHARE_MAX = 0.3;
export const ANCHOR_MAX_CHARS = 28;
/** apt/area/[region]/[sigungu] 의 색인 하한(단지 프로필 수) — 페이지 generateMetadata MIN_PROFILES 와 같은 값. */
export const AREA_INDEX_MIN_PROFILES = 10;

const GENERIC_RE = /^(바로\s*가기|더\s*보기|더보기|여기|여기를?\s*(클릭|눌러)?|자세히(\s*보기)?|전체\s*보기|보기|이동|클릭|링크|go|more|click here)\s*[→›>»]*\s*$/i;

/** 콘텐츠 링크로 쓰면 안 되는 «뜻 없는» 문구인가(화살표·공백 무시). */
export function isGenericAnchor(text: string | null | undefined): boolean {
  const t = String(text ?? '').replace(/[→›>»]/g, '').replace(/\s+/g, ' ').trim();
  return !t || GENERIC_RE.test(t);
}

const clean = (s: unknown): string => String(s ?? '').replace(/\s+/g, ' ').trim();
const len = (s: string): number => Array.from(s).length;

/** 키워드 반복(같은 낱말 2회 이상) 또는 길이 초과면 false. */
export function anchorIsClean(text: string): boolean {
  const t = clean(text);
  if (!t || len(t) > ANCHOR_MAX_CHARS || isGenericAnchor(t)) return false;
  const words = t.split(' ').filter((w) => w.length >= 2);
  return new Set(words).size === words.length;
}

/** (출발, 도착) → 자리 문구 종류(결정적). */
export function styleFor(source: string, target: string): AnchorStyle {
  const h = parseInt(fnv1a(`${clean(source)}→${clean(target)}`), 16) % 100;
  let acc = 0;
  for (const [s, w] of STYLE_MIX) { acc += w; if (h < acc) return s; }
  return 'branded';
}

export interface SiteAnchorInput {
  /** 현장 표시명(페이지 제목 선두와 같은 이름) */
  name: string;
  /** 키워드 지도 대표 검색어(「센텀 더샵 분양가」) — 없으면 이름 + 단계 꼬리 */
  primary?: string | null;
  /** 보조 검색어(이름 + 다른 꼬리) */
  secondaries?: readonly string[] | null;
  sigungu?: string | null;
  dong?: string | null;
  /** 「재개발」·「재건축」·「분양」 */
  typeWord?: string | null;
  stageLabel?: string | null;
}

function firstClean(cands: ReadonlyArray<string | null | undefined>, fallback: string): string {
  for (const c of cands) { const t = clean(c); if (t && anchorIsClean(t)) return t; }
  return clean(fallback);
}

/** 현장 상세로 가는 문구. */
export function siteAnchor(source: string, targetPath: string, s: SiteAnchorInput): { text: string; style: AnchorStyle } {
  const name = clean(s.name);
  const style = styleFor(source, targetPath);
  const tw = clean(s.typeWord) || '분양';
  const exact = clean(s.primary) || `${name} ${tw}`;
  const secs = (s.secondaries ?? []).map(clean).filter((x) => x && x !== exact && x.includes(name));
  const pick = (k: number) => (secs.length ? secs[parseInt(fnv1a(`${source}|${targetPath}|p`), 16) % secs.length] : null) ?? null;
  const place = clean(s.dong) || clean(s.sigungu);
  const text = style === 'exact' ? firstClean([exact], name)
    : style === 'partial' ? firstClean([pick(0), place && !name.includes(place) ? `${place} ${name}` : null, `${name} ${tw === '분양' ? '분양 일정' : `${tw} 진행`}`], name)
      : style === 'branded' ? name
        : firstClean([s.stageLabel ? `${name} ${clean(s.stageLabel)} 현황` : null, `${name} 단지 정보`], name);
  return { text, style };
}

/** 시군구 허브 문구 — 시군구 검색 꼬리(지도 대표 → 실측 전역 순서). */
export function districtAnchor(source: string, sido: string, sigungu: string, primary: readonly string[] = []): { text: string; style: AnchorStyle } {
  const place = districtPlaceOf(sido, sigungu);
  const target = `district:${sido}:${sigungu}`;
  const style = styleFor(source, target);
  const tails = DISTRICT_GLOBAL_ORDER;
  const h = parseInt(fnv1a(`${source}|${target}|t`), 16);
  const exact = clean(primary[0]) || `${place} ${tails[0]}`;
  const text = style === 'exact' ? firstClean([exact], place)
    : style === 'partial' ? firstClean([clean(primary[1 + (h % Math.max(1, primary.length - 1))]) || null, `${place} ${tails[1 + (h % 5)]}`], place)
      : style === 'branded' ? place
        : firstClean([`${place} 분양·정비 현장`, `${place} 아파트 분양 현황`], place);
  return { text, style };
}

/** 시도 허브 문구. */
export function regionAnchor(source: string, sido: string): { text: string; style: AnchorStyle } {
  const style = styleFor(source, `region:${sido}`);
  const s = clean(sido);
  const text = style === 'exact' ? `${s} 아파트 분양` : style === 'partial' ? `${s} 분양 일정` : style === 'branded' ? `${s} 부동산` : `${s} 분양·청약 현황`;
  return { text, style };
}

/** 글로 가는 문구 — 글 제목(쪽 제목과 같은 말) 그대로가 기본, 길면 «—» 앞 머리. */
export function postAnchor(title: string): string {
  const t = clean(title);
  if (len(t) <= 40) return t;
  const head = clean(t.split(/\s[—|-]\s/)[0]);
  return head && len(head) >= 6 ? head : Array.from(t).slice(0, 40).join('');
}

/** 목록 쪽(분석 글 목록 등)으로 가는 «더 보기» 자리 — 주제 이름을 넣는다(「{주제} 전체 보기」). */
export function listAnchor(topic: string): string {
  const t = clean(topic);
  return t ? `${t} 전체 보기` : '분석 글 전체 보기';
}

/**
 * 시군구 허브가 색인 제외면 시도 허브로(색인 제외 쪽에 링크 힘을 보내지 않는다).
 * profiles = 그 시군구 apt_complex_profiles 수(age_group 있는 행) — 모르면(null) 시군구 허브 그대로.
 */
export function hubTarget(sido: string, sigungu: string, profiles: number | null): { kind: 'district' | 'region'; href: string } {
  const sd = clean(sido), sg = clean(sigungu);
  if (!sg || (profiles != null && profiles < AREA_INDEX_MIN_PROFILES)) return { kind: 'region', href: `/apt/region/${encodeURIComponent(sd)}` };
  return { kind: 'district', href: `/apt/area/${encodeURIComponent(sd)}/${encodeURIComponent(sg)}` };
}

/** 한 쪽 안 «같은 도착 주소 앵커 1개» 장부. take(href) 가 false 면 링크 없이 글자만 쓴다. */
export class AnchorBudget {
  private seen = new Set<string>();
  static key(href: string): string {
    let h = String(href ?? '').trim().replace(/^https?:\/\/(www\.)?kadeora\.app/i, '');
    h = h.split('#')[0].split('?')[0];
    try { h = decodeURIComponent(h); } catch { /* 그대로 */ }
    return h.replace(/\/+$/, '') || '/';
  }
  take(href: string): boolean {
    const k = AnchorBudget.key(href);
    if (this.seen.has(k)) return false;
    this.seen.add(k);
    return true;
  }
  has(href: string): boolean { return this.seen.has(AnchorBudget.key(href)); }
}

/** 감사 — 앵커 묶음의 종류 분포·중복 도착·뜻 없는 문구 수. */
export function anchorStats(links: ReadonlyArray<{ href: string; text: string; style?: AnchorStyle }>): { total: number; generic: number; dupTargets: number; byStyle: Record<string, number> } {
  const per = new Map<string, number>();
  const byStyle: Record<string, number> = {};
  let generic = 0;
  for (const l of links) {
    const k = AnchorBudget.key(l.href);
    per.set(k, (per.get(k) ?? 0) + 1);
    if (isGenericAnchor(l.text)) generic++;
    if (l.style) byStyle[l.style] = (byStyle[l.style] ?? 0) + 1;
  }
  return { total: links.length, generic, dupTargets: [...per.values()].filter((n) => n > 1).length, byStyle };
}
